import type { Lens, Lifecycle } from './models/scorecard.types'
import type { ScorecardListSortField } from './models/scorecardList.types'

export interface ScorecardToggleOption<V> {
  value: V
  labelKey: string
  hintKey: string
}

export const LENS_OPTIONS: ScorecardToggleOption<Lens>[] = [
  {
    value: 'pace',
    labelKey: 'scorecards.lens.pace',
    hintKey: 'scorecards.lens.paceHint',
  },
  {
    value: 'forecast',
    labelKey: 'scorecards.lens.forecast',
    hintKey: 'scorecards.lens.forecastHint',
  },
]

export const LIFECYCLE_OPTIONS: ScorecardToggleOption<Lifecycle>[] = [
  {
    value: 'current',
    labelKey: 'scorecards.lifecycle.current',
    hintKey: 'scorecards.lifecycle.currentHint',
  },
  {
    value: 'completed',
    labelKey: 'scorecards.lifecycle.completed',
    hintKey: 'scorecards.lifecycle.completedHint',
  },
]

export const DEFAULT_LENS: Lens = 'pace'
export const DEFAULT_LIFECYCLE: Lifecycle = 'current'

/** The four view modes v3's host page offers (`views/App/Scorecards.vue:11–24`). */
export type ScorecardView = 'list' | 'grid' | 'chart' | 'rollup'

export interface ScorecardViewConfig {
  value: ScorecardView
  labelKey: string
  /**
   * Runtime availability belongs here so the tab strip and page use one source of truth. A future
   * remotely-disabled view can remain visible with an explicit unavailable state instead of
   * silently disappearing.
   */
  available: boolean
}

export const SCORECARD_VIEWS: readonly ScorecardViewConfig[] = [
  { value: 'list', labelKey: 'scorecards.views.list', available: true },
  { value: 'grid', labelKey: 'scorecards.views.grid', available: true },
  { value: 'chart', labelKey: 'scorecards.views.chart', available: true },
  { value: 'rollup', labelKey: 'scorecards.views.rollup', available: true },
]

export const DEFAULT_SCORECARD_VIEW: ScorecardView = 'list'

/** Per-board Grid column visibility; persisted UI state only, never server data. */
export function gridColumnsStorageKey(boardId: number): string {
  return `scorecards:grid:hidden-columns:${boardId}`
}

export interface ScorecardListColumnConfig {
  field: ScorecardListSortField
  labelKey: string
  hintKey: string
  width: number
  numeric?: boolean
  defaultHidden?: boolean
  frozen?: boolean
}

/** Contract 10 list schema: one centralized source for labels, order and default visibility. */
export const SCORECARD_LIST_COLUMNS: readonly ScorecardListColumnConfig[] = [
  {
    field: 'name',
    labelKey: 'scorecards.list.columns.name',
    hintKey: 'scorecards.list.hints.name',
    width: 260,
    frozen: true,
  },
  {
    field: 'period',
    labelKey: 'scorecards.list.columns.period',
    hintKey: 'scorecards.list.hints.period',
    width: 180,
    frozen: true,
  },
  {
    field: 'metric',
    labelKey: 'scorecards.list.columns.metric',
    hintKey: 'scorecards.list.hints.metric',
    width: 150,
  },
  {
    field: 'target',
    labelKey: 'scorecards.list.columns.target',
    hintKey: 'scorecards.list.hints.target',
    width: 130,
    numeric: true,
  },
  {
    field: 'actual',
    labelKey: 'scorecards.list.columns.actual',
    hintKey: 'scorecards.list.hints.actual',
    width: 130,
    numeric: true,
  },
  {
    field: 'pace',
    labelKey: 'scorecards.list.columns.pace',
    hintKey: 'scorecards.list.hints.pace',
    width: 120,
    numeric: true,
  },
  {
    field: 'completion',
    labelKey: 'scorecards.list.columns.completion',
    hintKey: 'scorecards.list.hints.completion',
    width: 130,
    numeric: true,
  },
  {
    field: 'forecast',
    labelKey: 'scorecards.list.columns.forecast',
    hintKey: 'scorecards.list.hints.forecast',
    width: 130,
    numeric: true,
    defaultHidden: true,
  },
  {
    field: 'forecastPct',
    labelKey: 'scorecards.list.columns.forecastPct',
    hintKey: 'scorecards.list.hints.forecastPct',
    width: 130,
    numeric: true,
    defaultHidden: true,
  },
  {
    field: 'remaining',
    labelKey: 'scorecards.list.columns.remaining',
    hintKey: 'scorecards.list.hints.remaining',
    width: 130,
    numeric: true,
  },
  {
    field: 'likelihood',
    labelKey: 'scorecards.list.columns.likelihood',
    hintKey: 'scorecards.list.hints.likelihood',
    width: 130,
  },
]

export interface ScorecardExportColumnConfig {
  field: string
  headerKey: string
  /** Raw (unformatted) number in the sheet, so the file stays round-trip friendly. */
  numeric?: boolean
}

/**
 * Contract 10 §3 export snapshot. The lens-dependent pairs collapse into one `achieved` and one
 * `onTrackPct` column that follow the active lens, so the file mirrors what the user is looking at.
 */
export const SCORECARD_EXPORT_COLUMNS: readonly ScorecardExportColumnConfig[] = [
  { field: 'name', headerKey: 'scorecards.export.columns.name' },
  { field: 'metric', headerKey: 'scorecards.export.columns.metric' },
  { field: 'breakdown', headerKey: 'scorecards.export.columns.breakdown' },
  { field: 'period', headerKey: 'scorecards.export.columns.period' },
  { field: 'target', headerKey: 'scorecards.export.columns.target', numeric: true },
  { field: 'achieved', headerKey: 'scorecards.export.columns.achieved', numeric: true },
  { field: 'remaining', headerKey: 'scorecards.export.columns.remaining', numeric: true },
  { field: 'onTrackPct', headerKey: 'scorecards.export.columns.onTrackPct', numeric: true },
  { field: 'progressPct', headerKey: 'scorecards.export.columns.progressPct', numeric: true },
  { field: 'status', headerKey: 'scorecards.export.columns.status' },
  { field: 'likelihood', headerKey: 'scorecards.export.columns.likelihood' },
]

/** Grid's per-metric sub-columns (v3 `ALL_SUB_COLS`) — one set repeats under every visible metric. */
export type GridSubColKey =
  'target' | 'actual' | 'progress' | 'pace' | 'forecast' | 'forecast_pct' | 'remaining'

export interface GridSubColumnConfig {
  key: GridSubColKey
  labelKey: string
  tooltipKey: string
}

/** Rendered as a colored, arrow-badge pill — same treatment as List's Pace/Completion/Forecast %. */
export const GRID_PCT_SUB_COLUMNS: ReadonlySet<GridSubColKey> = new Set([
  'progress',
  'pace',
  'forecast_pct',
])

export const GRID_SUB_COLUMNS: readonly GridSubColumnConfig[] = [
  {
    key: 'target',
    labelKey: 'scorecards.grid.subColumns.target',
    tooltipKey: 'scorecards.grid.subColumns.targetTooltip',
  },
  {
    key: 'actual',
    labelKey: 'scorecards.grid.subColumns.actual',
    tooltipKey: 'scorecards.grid.subColumns.actualTooltip',
  },
  {
    key: 'progress',
    labelKey: 'scorecards.grid.subColumns.progress',
    tooltipKey: 'scorecards.grid.subColumns.progressTooltip',
  },
  {
    key: 'pace',
    labelKey: 'scorecards.grid.subColumns.pace',
    tooltipKey: 'scorecards.grid.subColumns.paceTooltip',
  },
  {
    key: 'forecast',
    labelKey: 'scorecards.grid.subColumns.forecast',
    tooltipKey: 'scorecards.grid.subColumns.forecastTooltip',
  },
  {
    key: 'forecast_pct',
    labelKey: 'scorecards.grid.subColumns.forecastPct',
    tooltipKey: 'scorecards.grid.subColumns.forecastPctTooltip',
  },
  {
    key: 'remaining',
    labelKey: 'scorecards.grid.subColumns.remaining',
    tooltipKey: 'scorecards.grid.subColumns.remainingTooltip',
  },
]

export const DEFAULT_HIDDEN_GRID_SUB_COLUMNS: readonly GridSubColKey[] = [
  'forecast',
  'forecast_pct',
  'remaining',
]

/** Roll-up's toggleable stat columns (v3 `TOGGLEABLE_COLS`) — Actual is fixed, never hidden. */
export type RollupColumnKey = 'target' | 'completion' | 'pace' | 'remaining'

export interface RollupColumnConfig {
  key: RollupColumnKey
  labelKey: string
  tooltipKey?: string
}

export const ROLLUP_COLUMNS: readonly RollupColumnConfig[] = [
  { key: 'target', labelKey: 'scorecards.rollup.columns.target' },
  {
    key: 'completion',
    labelKey: 'scorecards.rollup.columns.completion',
    tooltipKey: 'scorecards.rollup.columns.completionTooltip',
  },
  {
    key: 'pace',
    labelKey: 'scorecards.rollup.columns.pace',
    tooltipKey: 'scorecards.rollup.columns.paceTooltip',
  },
  {
    key: 'remaining',
    labelKey: 'scorecards.rollup.columns.remaining',
    tooltipKey: 'scorecards.rollup.columns.remainingTooltip',
  },
]
